/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useEffect, useRef } from "react";
import "./Header.scss";
import {
  Avatar,
  Menu,
  IconButton,
  Box,
  Divider,
  Tooltip,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import React from "react";
import GlobalSearchBox from "../../components/form/GlobalSearchBox/GlobalSearchBox";
import { useTheme } from "@mui/material/styles";
import LogoutButton from "../../pages/logout/Logout";
import { brown } from "@mui/material/colors";
import { useUser } from "../../context/UserContext";
import NotificationPopover from "../../features/messages/NotificationPopover";
import MessagesIcon from "../../features/messages/internalMessages/MessagesIcon";
import ImpersonationBanner from "../../pages/Admin/Manage/ImpersonationBanner";
import PersonAddAltTwoToneIcon from "@mui/icons-material/PersonAddAltTwoTone";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { IMPERSONATION_INFO, ModuleNames, LOCALSTORAGENAME } from "../../data/constants";
import { useMenu } from "../../context/MenuContext";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import ContactPage from "../../pages/contacts/ContactPage";
import { Link } from "react-router-dom";
import { INTERNALMESSAGES } from "../../constants/messages";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../utils/dateHelper";
import { getUserDisplayName } from "../../utils/commonHelper";
import useLogout from "../../hooks/useLogout";

interface HeaderProps {
  onMenuClick: () => void;
  isMobile: boolean;
  isOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, isMobile, isOpen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuRef = useRef(null);
  const { isImpersonating } = useSelector((state: RootState) => state.impersonation);
  const { userName, userId, firstName, lastName, profileImage } = useUser();
  const { permissions } = useMenu();
  const { logoutWithAzure } = useLogout();
  const [displayMessage, setDisplayMessage] = useState(false);
  const [displayNotification, setDisplayNotification] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showContactPage, setShowContactPage] = useState(false);
  const [displayHelp, setDisplayHelp] = useState<boolean>(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const theme = useTheme();
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const isMessageEnable = permissions?.find(
        (permission) => permission.moduleName === ModuleNames.Messages,
      )?.canView;
      setDisplayMessage(isMessageEnable);
      const isNotificationEnable = permissions?.find(
        (permission) => permission.moduleName === ModuleNames.Notifications,
      )?.canView;
      setDisplayNotification(isNotificationEnable);

      // help display
      const isHelpEnable =
        permissions.find((p) => p.moduleName === ModuleNames.Contacts)?.canView ?? false;

      setDisplayHelp(isHelpEnable);
    }
  }, [permissions]);

  function stringAvatar(firstName?: string, lastName?: string, userName?: string) {
    let name = "";

    if (firstName || lastName) {
      name = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    } else if (userName) {
      name = userName;
    }

    if (!name) return { children: "" };

    const words = name.split(/\s+/);

    const firstInitial = words[0]?.[0] || "";
    const secondInitial = words[1]?.[0] || "";

    return {
      children: `${firstInitial}${secondInitial}`.toUpperCase(),
    };
  }

  // `isOpen` is the raw drawer flag, and Sidenav.scss reads it in opposite directions per
  // breakpoint: `.open.mobile-sidenav` slides the panel in, `.open.desktop-sidenav` slides it out.
  // Resolve it to actual visibility here so the arrow points the right way on both.
  const isSidenavVisible = isMobile ? isOpen : !isOpen;

  const handleMenuClick = () => {
    setShowTooltip(false);
    onMenuClick();
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  return (
    <>
      <Box>
        {isImpersonating && (
          <Box
            // Sits outside .header, so global.scss hides it for print by this class (Bug 83110).
            className="header-impersonation"
            sx={{
              position: "fixed",
              top: 0,
              left: {
                xs: 0,
                md: isOpen ? 0 : 280,
              },
              right: 0,
              zIndex: 1003,
              backgroundColor: "#fff3cd",
              color: "#856404",
              height: "40px",
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              fontSize: "0.9rem",
              fontWeight: 500,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <IconButton
              disableRipple
              disableFocusRipple
              sx={{
                backgroundColor: "#f47c2b",
                width: 28,
                height: 28,
                padding: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#f47c2b",
                },
              }}
            >
              <img
                src="/Impersonate.gif"
                width={18}
                height={18}
                alt="Impersonation"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </IconButton>

            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {localStorage.getItem(IMPERSONATION_INFO.ImpersonatedMessage)}
            </Box>
          </Box>
        )}

        <div
          className={`header ${isMobile ? "header--small" : "header--big"} ${isImpersonating ? "header--impersonating" : ""} ${isOpen ? "open" : ""}`}
        >
          <div className="header-left">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleMenuClick}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={handleMouseEnter}
              edge="start"
              sx={{
                mr: 0,
                color: theme.palette.common.white,
              }}
            >
              {isSidenavVisible ? (
                <Tooltip title="Collapse menu" arrow open={showTooltip}>
                  <KeyboardDoubleArrowLeftIcon />
                </Tooltip>
              ) : (
                <Tooltip title="Expand menu" arrow open={showTooltip}>
                  <KeyboardDoubleArrowRightIcon />
                </Tooltip>
              )}
            </IconButton>

            <Box
              sx={{
                flexGrow: 1,
                maxWidth: "70%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <GlobalSearchBox />
            </Box>
          </div>
          <div className="header-right">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {displayNotification ? <NotificationPopover /> : null}
              {displayMessage ? <MessagesIcon /> : null}
              {displayHelp ? (
                <Tooltip title={INTERNALMESSAGES.HELP}>
                  <IconButton
                    color="inherit"
                    onClick={() => {
                      logAuditTrace({
                        userId: userId ?? 0,
                        objectType: "Page",
                        objectId: null,
                        objectName: "Navigated to Help & Support",
                        action: "Navigated",
                        navigationDateTime: getCurrentESTTime(),
                      });
                      setShowContactPage(true);
                    }}
                  >
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
              ) : null}

              {isImpersonating ? (
                <ImpersonationBanner />
              ) : (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                  ref={menuRef}
                  className="user-profile flex items-center cursor-pointer relative"
                  onClick={handleClick}
                >
                  <Avatar
                    {...stringAvatar(firstName, lastName, userName)}
                    className="user-profile-img"
                    sx={{ bgcolor: brown[700] }}
                    src={profileImage ?? undefined}
                  />
                  <div className="me-1 user-detail-info">
                    <span className="user-name-text">
                      {getUserDisplayName(userName, firstName, lastName)}
                    </span>
                    <span
                      className="user-name-sub-text"
                      title={localStorage.getItem(LOCALSTORAGENAME.ROLENAMES)}
                    >
                      {(localStorage.getItem(LOCALSTORAGENAME.ROLENAMES) || "").split(",")[0] || ""}
                      {(localStorage.getItem(LOCALSTORAGENAME.ROLENAMES) || "").split(",").length >
                        1 && ",.."}
                    </span>
                  </div>
                  <KeyboardArrowDownIcon className="ml-2 text-gray-600" />
                  {!displayHelp ? (
                    <Menu
                      disableScrollLock
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      onClick={handleClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          className: "custom-menu-paper custom-menu-Logout",
                        },
                      }}
                    >
                      <LogoutButton />
                    </Menu>
                  ) : (
                    <Menu
                      disableScrollLock
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      onClick={handleClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          className: "custom-menu-paper",
                        },
                      }}
                    >
                      <Box className="signout-section">
                        <p className="org-title">Hamaspik of Kings County Inc</p>
                        <LogoutButton />
                      </Box>

                      <div className="user-profile-dropdown">
                        <Avatar
                          {...stringAvatar(firstName, lastName, userName)}
                          className="user-profile-img"
                          sx={{ bgcolor: brown[700] }}
                          src={profileImage ?? undefined}
                        />
                        <div className="me-1 user-detail-info">
                          <span
                            className="user-name-text"
                            title={getUserDisplayName(userName, firstName, lastName)}
                          >
                            {getUserDisplayName(userName, firstName, lastName)}
                          </span>
                          <span
                            className="user-name-sub-text"
                            title={localStorage.getItem(LOCALSTORAGENAME.ROLENAMES)}
                          >
                            {(localStorage.getItem(LOCALSTORAGENAME.ROLENAMES) || "").split(
                              ",",
                            )[0] || ""}
                            {(localStorage.getItem(LOCALSTORAGENAME.ROLENAMES) || "").split(",")
                              .length > 1 && ",..."}
                          </span>
                          <Link to="/user-profile" className="link">
                            View account
                          </Link>
                        </div>
                      </div>

                      <Divider component="li" />

                      <MenuItem
                        onClick={() => logoutWithAzure("User Id ")}
                        className="sign-in-account"
                      >
                        <ListItemIcon>
                          <PersonAddAltTwoToneIcon />
                        </ListItemIcon>
                        <ListItemText>Sign in with a different account</ListItemText>
                      </MenuItem>
                    </Menu>
                  )}
                </div>
              )}
            </Box>
          </div>
        </div>
      </Box>

      {/* Contact Page Dialog */}
      <Dialog
        open={showContactPage}
        onClose={() => setShowContactPage(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Contact Support
          <IconButton
            aria-label="close"
            onClick={() => setShowContactPage(false)}
            className="dialog-close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <ContactPage onSubmitSuccess={() => setShowContactPage(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};
export default Header;
